import { useState, useEffect, useCallback } from 'react';
import { Instagram, Linkedin, Youtube, Facebook, Clock } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { TikTokIcon } from '@/components/ui/TikTokIcon';
import { getUserPlanningPreferences, upsertPlanningSetup } from '@/services/data/planningPreferences';
import { getUserPlan } from '@/services/data/users';
import { getAllPlatforms, type ConnectedPlatform } from '@/services/data/socialConnections';
import type { PlatformChannel, PlatformSchedule, PlatformSchedules } from '@/types/scheduling';
import { PLAN_CONFIG, isEmergencePlan, type PlanKey } from '@/lib/planFrequency';

const DAYS = [{
  value: 1,
  label: 'Lun',
  fullLabel: 'Lundi'
}, {
  value: 2,
  label: 'Mar',
  fullLabel: 'Mardi'
}, {
  value: 3,
  label: 'Mer',
  fullLabel: 'Mercredi'
}, {
  value: 4,
  label: 'Jeu',
  fullLabel: 'Jeudi'
}, {
  value: 5,
  label: 'Ven',
  fullLabel: 'Vendredi'
}, {
  value: 6,
  label: 'Sam',
  fullLabel: 'Samedi'
}, {
  value: 0,
  label: 'Dim',
  fullLabel: 'Dimanche'
}];
const PLATFORMS: Array<{
  key: PlatformChannel;
  label: string;
  icon: any;
  color: string;
}> = [{
  key: 'instagram',
  label: 'Instagram',
  icon: Instagram,
  color: 'text-pink-400'
}, {
  key: 'tiktok',
  label: 'TikTok',
  icon: TikTokIcon,
  color: 'text-white'
}, {
  key: 'youtube',
  label: 'YouTube',
  icon: Youtube,
  color: 'text-red-400'
}, {
  key: 'facebook',
  label: 'Facebook',
  icon: Facebook,
  color: 'text-blue-400'
}, {
  key: 'linkedin',
  label: 'LinkedIn',
  icon: Linkedin,
  color: 'text-sky-400'
}];
const TIME_OPTIONS = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'];
const DEFAULT_PLATFORM_SCHEDULE: PlatformSchedule = {
  days: [],
  time: '18:00'
};

// Reusable DayButton component
const DayButton = ({
  label,
  isSelected,
  isDisabled,
  onClick,
  variant = 'weekA'
}: Readonly<{
  label: string;
  isSelected: boolean;
  isDisabled: boolean;
  onClick: () => void;
  variant?: 'weekA' | 'weekB';
}>) => {
  const selectedColor = variant === 'weekA' ? 'bg-warning text-black' : 'bg-warning/70 text-black';
  const disabledClass = 'bg-white/5 border border-white/10 text-white/20 cursor-not-allowed';
  const defaultClass = 'bg-white/5 border border-white/20 text-white/60 hover:border-white/40';

  let buttonClass: string;
  if (isSelected) {
    buttonClass = selectedColor;
  } else if (isDisabled) {
    buttonClass = disabledClass;
  } else {
    buttonClass = defaultClass;
  }

  return <button onClick={onClick} disabled={isDisabled} className={`w-8 h-8 rounded-md text-xs font-medium transition-all ${buttonClass}`}>
      {label}
    </button>;
};
interface ManualPlanningConfigProps {
  readonly userId: string;
  readonly onSave?: () => void;
  readonly compact?: boolean;
}
export function ManualPlanningConfig({
  userId,
  onSave,
  compact = false
}: Readonly<ManualPlanningConfigProps>) {
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [globalFrequency, setGlobalFrequency] = useState<number>(3);
  const [platformSchedules, setPlatformSchedules] = useState<PlatformSchedules>({});
  const [weekBSchedules, setWeekBSchedules] = useState<PlatformSchedules>({});
  const [isUnifiedMode, setIsUnifiedMode] = useState<boolean>(true);
  const [unifiedSchedule, setUnifiedSchedule] = useState<PlatformSchedule>({
    ...DEFAULT_PLATFORM_SCHEDULE
  });
  const [unifiedWeekBSchedule, setUnifiedWeekBSchedule] = useState<PlatformSchedule>({
    ...DEFAULT_PLATFORM_SCHEDULE
  });
  const [isLoading, setIsLoading] = useState(true);
  const [activePlatforms, setActivePlatforms] = useState<Set<string>>(new Set());

  // Load active and connected platforms from social_connections
  useEffect(() => {
    const loadPlatforms = async () => {
      if (!userId) return;
      const result = await getAllPlatforms(userId);
      if (result.success && result.data) {
        const activeSet = new Set(
          result.data
            .filter((p: ConnectedPlatform) => p.is_active)
            .map((p: ConnectedPlatform) => p.platform.toLowerCase())
        );
        setActivePlatforms(activeSet);
      }
    };
    loadPlatforms();
  }, [userId]);

  const isPlatformActive = useCallback((platformKey: string) => {
    return activePlatforms.has(platformKey.toLowerCase());
  }, [activePlatforms]);

  // Build schedules for saving
  const buildSchedulesToSave = useCallback(() => {
    let schedulesToSave: PlatformSchedules;
    let weekBToSave: PlatformSchedules;
    if (isUnifiedMode) {
      const unifiedForAll: PlatformSchedules = {};
      const weekBForAll: PlatformSchedules = {};
      PLATFORMS.forEach(p => {
        unifiedForAll[p.key] = {
          ...unifiedSchedule
        };
        weekBForAll[p.key] = {
          ...unifiedWeekBSchedule
        };
      });
      schedulesToSave = unifiedForAll;
      weekBToSave = weekBForAll;
    } else {
      schedulesToSave = platformSchedules;
      weekBToSave = weekBSchedules;
    }
    const combinedSchedules = {
      ...schedulesToSave
    };
    (combinedSchedules as any)._weekB = weekBToSave;
    (combinedSchedules as any)._isUnifiedMode = isUnifiedMode;
    (combinedSchedules as any)._unifiedSchedule = unifiedSchedule;
    (combinedSchedules as any)._unifiedWeekBSchedule = unifiedWeekBSchedule;
    return combinedSchedules;
  }, [isUnifiedMode, unifiedSchedule, unifiedWeekBSchedule, platformSchedules, weekBSchedules]);

  // Auto-save on change
  const savePreferences = useCallback(async () => {
    if (!userId) return;
    const combinedSchedules = buildSchedulesToSave();
    await upsertPlanningSetup(userId, globalFrequency, combinedSchedules, false);
    onSave?.();
  }, [userId, globalFrequency, buildSchedulesToSave, onSave]);

  // Load preferences on mount
  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;
      setIsLoading(true);

      // Load user's plan
      const planResult = await getUserPlan(userId);
      if (planResult.success && planResult.data) {
        setUserPlan(planResult.data);
        const config = PLAN_CONFIG[planResult.data as PlanKey];
        if (config) {
          setGlobalFrequency(config.frequency);
        }
      }

      // Load saved preferences
      const result = await getUserPlanningPreferences(userId);
      if (result.success && result.data) {
        const schedules = result.data.platform_schedules || {};
        const weekB = (schedules as any)._weekB || {};
        const savedIsUnifiedMode = (schedules as any)._isUnifiedMode;
        const savedUnifiedSchedule = (schedules as any)._unifiedSchedule;
        const savedUnifiedWeekBSchedule = (schedules as any)._unifiedWeekBSchedule;
        delete (schedules as any)._weekB;
        delete (schedules as any)._isUnifiedMode;
        delete (schedules as any)._unifiedSchedule;
        delete (schedules as any)._unifiedWeekBSchedule;
        setPlatformSchedules(schedules);
        setWeekBSchedules(weekB);
        if (savedIsUnifiedMode !== undefined) setIsUnifiedMode(savedIsUnifiedMode);
        if (savedUnifiedSchedule) setUnifiedSchedule(savedUnifiedSchedule);
        if (savedUnifiedWeekBSchedule) setUnifiedWeekBSchedule(savedUnifiedWeekBSchedule);
      }
      setIsLoading(false);
    };
    loadData();
  }, [userId]);

  // Save when schedules change
  useEffect(() => {
    if (!isLoading && userId) {
      const timeout = setTimeout(() => {
        savePreferences();
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [unifiedSchedule, unifiedWeekBSchedule, platformSchedules, weekBSchedules, isUnifiedMode, isLoading, savePreferences, userId]);
  const isAlternatingPlan = isEmergencePlan(userPlan);
  const weekAMaxDays = isAlternatingPlan ? 3 : globalFrequency;
  const weekBMaxDays = 2;

  // Unified mode functions
  const toggleUnifiedDay = (day: number, week: 'A' | 'B' = 'A') => {
    const setSchedule = week === 'A' ? setUnifiedSchedule : setUnifiedWeekBSchedule;
    const maxDays = week === 'A' ? weekAMaxDays : weekBMaxDays;
    setSchedule(prev => {
      const isRemoving = prev.days.includes(day);
      if (isRemoving) {
        return {
          ...prev,
          days: prev.days.filter(d => d !== day)
        };
      }
      if (prev.days.length >= maxDays) return prev;
      return {
        ...prev,
        days: [...prev.days, day].sort((a, b) => a - b)
      };
    });
  };
  const setUnifiedTime = (time: string, day?: number) => {
    setUnifiedSchedule(prev => {
      if (day !== undefined) {
        return {
          ...prev,
          dayTimes: {
            ...prev.dayTimes,
            [day]: time
          }
        };
      }
      return {
        ...prev,
        time
      };
    });
  };
  const getAllUnifiedDays = (): number[] => {
    const weekADays = unifiedSchedule.days || [];
    const weekBDays = unifiedWeekBSchedule.days || [];
    const allDays = [...new Set([...weekADays, ...weekBDays])];
    return allDays.sort((a, b) => {
      const orderA = a === 0 ? 7 : a;
      const orderB = b === 0 ? 7 : b;
      return orderA - orderB;
    });
  };
  const hasUnifiedPerDayTimes = () => {
    return !!(unifiedSchedule.dayTimes && Object.keys(unifiedSchedule.dayTimes).length > 0);
  };
  const getUnifiedDayTime = (day: number) => {
    return unifiedSchedule.dayTimes?.[day] || unifiedSchedule.time || '18:00';
  };
  const enableUnifiedPerDayTime = () => {
    const dayTimes: Record<number, string> = {};
    const defaultTime = unifiedSchedule.time || '18:00';
    getAllUnifiedDays().forEach(d => {
      dayTimes[d] = defaultTime;
    });
    setUnifiedSchedule(prev => ({
      ...prev,
      dayTimes
    }));
  };

  const disableUnifiedPerDayTime = () => {
    setUnifiedSchedule(prev => {
      const {
        dayTimes,
        ...rest
      } = prev;
      return rest;
    });
  };

  const toggleUnifiedPerDayTime = (enabled: boolean) => {
    if (enabled) {
      enableUnifiedPerDayTime();
    } else {
      disableUnifiedPerDayTime();
    }
  };

  // Per-platform mode functions
  const toggleDay = (platform: PlatformChannel, day: number, week: 'A' | 'B' = 'A') => {
    const setSchedules = week === 'A' ? setPlatformSchedules : setWeekBSchedules;
    const maxDays = week === 'A' ? weekAMaxDays : weekBMaxDays;
    setSchedules(prev => {
      const current = prev[platform] || {
        ...DEFAULT_PLATFORM_SCHEDULE
      };
      const isRemoving = current.days.includes(day);
      if (isRemoving) {
        return {
          ...prev,
          [platform]: {
            ...current,
            days: current.days.filter(d => d !== day)
          }
        };
      }
      if (current.days.length >= maxDays) return prev;
      return {
        ...prev,
        [platform]: {
          ...current,
          days: [...current.days, day].sort((a, b) => a - b)
        }
      };
    });
  };
  const setPlatformTime = (platform: PlatformChannel, time: string, day?: number) => {
    setPlatformSchedules(prev => {
      const current = prev[platform] || {
        ...DEFAULT_PLATFORM_SCHEDULE
      };
      if (day !== undefined) {
        return {
          ...prev,
          [platform]: {
            ...current,
            dayTimes: {
              ...current.dayTimes,
              [day]: time
            }
          }
        };
      }
      return {
        ...prev,
        [platform]: {
          ...current,
          time
        }
      };
    });
  };
  const hasPlatformPerDayTimes = (platform: PlatformChannel) => {
    const schedule = platformSchedules[platform];
    return !!(schedule?.dayTimes && Object.keys(schedule.dayTimes).length > 0);
  };
  const getPlatformDayTime = (platform: PlatformChannel, day: number) => {
    const schedule = platformSchedules[platform];
    return schedule?.dayTimes?.[day] || schedule?.time || '18:00';
  };
  const enablePlatformPerDayTime = (platform: PlatformChannel) => {
    setPlatformSchedules(prev => {
      const current = prev[platform] || {
        ...DEFAULT_PLATFORM_SCHEDULE
      };
      const dayTimes: Record<number, string> = {};
      const defaultTime = current.time || '18:00';
      current.days.forEach(d => {
        dayTimes[d] = defaultTime;
      });
      return {
        ...prev,
        [platform]: {
          ...current,
          dayTimes
        }
      };
    });
  };

  const disablePlatformPerDayTime = (platform: PlatformChannel) => {
    setPlatformSchedules(prev => {
      const current = prev[platform] || {
        ...DEFAULT_PLATFORM_SCHEDULE
      };
      const {
        dayTimes,
        ...rest
      } = current;
      return {
        ...prev,
        [platform]: rest
      };
    });
  };

  const togglePlatformPerDayTime = (platform: PlatformChannel, enabled: boolean) => {
    if (enabled) {
      enablePlatformPerDayTime(platform);
    } else {
      disablePlatformPerDayTime(platform);
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-6">
        <div className="h-6 w-6 border-2 border-white/20 border-t-amber-400 rounded-full animate-spin" />
      </div>;
  }
  return <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-warning" />
          <span className="text-sm font-medium text-warning">Jours et heures de publication</span>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="flex items-center gap-2">
        <Checkbox id="per-platform-mode-onboarding" checked={!isUnifiedMode} onCheckedChange={checked => setIsUnifiedMode(!checked)} />
        <label htmlFor="per-platform-mode-onboarding" className="text-sm text-white/60 cursor-pointer">
          Détailler par réseau
        </label>
      </div>

      {/* Schedules */}
      {isUnifiedMode && (/* UNIFIED MODE */
    <div className="rounded-xl p-4 space-y-4 bg-white/[0.03] border border-white/10">
          {/* Platform icons */}
          <div className="flex items-center gap-2 pb-3 border-b border-white/10">
            {PLATFORMS.map(platform => {
              const Icon = platform.icon;
               const isActive = isPlatformActive(platform.key);
               return <Icon key={platform.key} className={`h-4 w-4 ${isActive ? 'text-warning' : 'text-white/40'}`} />;
            })}
          </div>

          {/* Days selection */}
          {isAlternatingPlan ? <div className="space-y-4">
              {/* Week A */}
              <div className="space-y-2">
                <Label className="text-xs text-white/50 uppercase tracking-wide">
                  Semaine A - 3 jours ({unifiedSchedule.days.length}/3)
                </Label>
                <div className="flex gap-1.5 flex-wrap">
                  {DAYS.map(day => {
              const isSelected = unifiedSchedule.days.includes(day.value);
              const isDisabled = !isSelected && unifiedSchedule.days.length >= 3;
              return <DayButton key={day.value} label={day.label} isSelected={isSelected} isDisabled={isDisabled} onClick={() => toggleUnifiedDay(day.value, 'A')} variant="weekA" />;
            })}
                </div>
              </div>

              {/* Week B */}
              <div className="space-y-2">
                <Label className="text-xs text-amber-400/70 uppercase tracking-wide">
                  Semaine B - 2 jours ({unifiedWeekBSchedule.days.length}/2)
                </Label>
                <div className="flex gap-1.5 flex-wrap">
                  {DAYS.map(day => {
              const isSelected = unifiedWeekBSchedule.days.includes(day.value);
              const isDisabled = !isSelected && unifiedWeekBSchedule.days.length >= 2;
              return <DayButton key={day.value} label={day.label} isSelected={isSelected} isDisabled={isDisabled} onClick={() => toggleUnifiedDay(day.value, 'B')} variant="weekB" />;
            })}
                </div>
              </div>
            </div> : <div className="space-y-2">
              <Label className="text-xs text-white/50 uppercase tracking-wide">
                Jours ({unifiedSchedule.days.length}/{weekAMaxDays}) — Plan {userPlan ? PLAN_CONFIG[userPlan as PlanKey]?.labelShort || userPlan : 'Non défini'}
              </Label>
              <div className="flex gap-1.5 flex-wrap">
                {DAYS.map(day => {
            const isSelected = unifiedSchedule.days.includes(day.value);
            const isDisabled = !isSelected && unifiedSchedule.days.length >= weekAMaxDays;
            return <DayButton key={day.value} label={day.label} isSelected={isSelected} isDisabled={isDisabled} onClick={() => toggleUnifiedDay(day.value, 'A')} variant="weekA" />;
          })}
              </div>
            </div>}

          {/* Time selection */}
          {(() => {
            const allDays = getAllUnifiedDays();
            const shouldShowTimesSection = allDays.length > 0;
            const hasPerDayTimes = hasUnifiedPerDayTimes();

            if (!shouldShowTimesSection) return null;

            return <div className="space-y-3 pt-3 border-t border-white/10">
                <Label className="text-xs text-white/50 uppercase tracking-wide">Heure de publication</Label>

                {allDays.length > 1 && <div className="flex items-center gap-2">
                    <Checkbox id="different-times-onboarding" checked={hasPerDayTimes} onCheckedChange={toggleUnifiedPerDayTime} />
                    <label htmlFor="different-times-onboarding" className="text-sm text-white/60 cursor-pointer">
                      Heures différentes par jour
                    </label>
                  </div>}

                {hasPerDayTimes && <div className="space-y-2">
                    {allDays.map(dayValue => {
              const dayInfo = DAYS.find(d => d.value === dayValue);
              return <div key={dayValue} className="flex items-center gap-3">
                          <span className="text-sm text-white/60 w-20">{dayInfo?.fullLabel}</span>
                          <Select value={getUnifiedDayTime(dayValue)} onValueChange={time => setUnifiedTime(time, dayValue)}>
                            <SelectTrigger className="w-24 h-8 text-sm bg-white/5 border-white/20">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 z-[100] pointer-events-auto" position="popper" sideOffset={4}>
                              {TIME_OPTIONS.map(time => <SelectItem key={time} value={time}>{time}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>;
            })}
                  </div>}

                {!hasPerDayTimes && <Select value={unifiedSchedule.time || '18:00'} onValueChange={time => setUnifiedTime(time)}>
                    <SelectTrigger className="w-32 bg-white/5 border-white/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 z-[100] pointer-events-auto" position="popper" sideOffset={4}>
                      {TIME_OPTIONS.map(time => <SelectItem key={time} value={time}>{time}</SelectItem>)}
                    </SelectContent>
                  </Select>}
              </div>;
          })()}
        </div>)}

      {!isUnifiedMode && (/* PER-PLATFORM MODE */
    <Accordion type="single" collapsible className="space-y-2">
          {PLATFORMS.map(platform => {
        const schedule = platformSchedules[platform.key] || DEFAULT_PLATFORM_SCHEDULE;
        const daysCount = schedule.days.length;
        const maxDays = weekAMaxDays;
        const isActive = isPlatformActive(platform.key);

        let statusText: string;
        if (isActive) {
          statusText = daysCount > 0 ? `${daysCount}/${maxDays} jours` : 'Non configuré';
        } else {
          statusText = 'Non activé';
        }

        return <AccordionItem
          key={platform.key}
          value={platform.key}
          className={`rounded-xl border px-4 ${isActive ? 'bg-white/[0.03] border-white/10' : 'bg-white/[0.01] border-white/5 opacity-40 pointer-events-none'}`}
          disabled={!isActive}
        >
                <AccordionTrigger className="py-3 hover:no-underline" disabled={!isActive}>
                  <div className="flex items-center gap-3">

                    <span className={`font-medium text-sm ${isActive ? 'text-white' : 'text-white/40'}`}>{platform.label}</span>
                    <span className="text-xs text-white/40 ml-2">
                      {statusText}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 space-y-4">
                  {/* Days selection */}
                  {isAlternatingPlan ? <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-white/50 uppercase tracking-wide">
                          Semaine A ({schedule.days.length}/3)
                        </Label>
                        <div className="flex gap-1.5 flex-wrap">
                          {DAYS.map(day => {
                    const isSelected = schedule.days.includes(day.value);
                    const isDisabled = !isSelected && schedule.days.length >= 3;
                    return <DayButton key={day.value} label={day.label} isSelected={isSelected} isDisabled={isDisabled} onClick={() => toggleDay(platform.key, day.value, 'A')} variant="weekA" />;
                  })}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-amber-400/70 uppercase tracking-wide">
                          Semaine B ({(weekBSchedules[platform.key]?.days || []).length}/2)
                        </Label>
                        <div className="flex gap-1.5 flex-wrap">
                          {DAYS.map(day => {
                    const weekBSchedule = weekBSchedules[platform.key] || DEFAULT_PLATFORM_SCHEDULE;
                    const isSelected = weekBSchedule.days.includes(day.value);
                    const isDisabled = !isSelected && weekBSchedule.days.length >= 2;
                    return <DayButton key={day.value} label={day.label} isSelected={isSelected} isDisabled={isDisabled} onClick={() => toggleDay(platform.key, day.value, 'B')} variant="weekB" />;
                  })}
                        </div>
                      </div>
                    </div> : <div className="space-y-2">
                      <Label className="text-xs text-white/50 uppercase tracking-wide">
                        Jours ({schedule.days.length}/{weekAMaxDays})
                      </Label>
                      <div className="flex gap-1.5 flex-wrap">
                        {DAYS.map(day => {
                  const isSelected = schedule.days.includes(day.value);
                  const isDisabled = !isSelected && schedule.days.length >= weekAMaxDays;
                  return <DayButton key={day.value} label={day.label} isSelected={isSelected} isDisabled={isDisabled} onClick={() => toggleDay(platform.key, day.value, 'A')} variant="weekA" />;
                })}
                      </div>
                    </div>}

                  {/* Time selection per platform */}
                  {schedule.days.length > 0 && <div className="pt-3 border-t border-white/10 space-y-3">
                      <Label className="text-xs text-white/50 uppercase tracking-wide">Heure de publication</Label>

                      {schedule.days.length > 1 && <div className="flex items-center gap-2">
                          <Checkbox id={`different-times-${platform.key}`} checked={hasPlatformPerDayTimes(platform.key)} onCheckedChange={checked => togglePlatformPerDayTime(platform.key, !!checked)} />
                          <label htmlFor={`different-times-${platform.key}`} className="text-sm text-white/60 cursor-pointer">
                            Heures différentes par jour
                          </label>
                        </div>}

                      {hasPlatformPerDayTimes(platform.key) ? <div className="space-y-2">
                          {schedule.days.map(dayValue => {
                  const dayInfo = DAYS.find(d => d.value === dayValue);
                  return <div key={dayValue} className="flex items-center gap-3">
                                <span className="text-sm text-white/60 w-20">{dayInfo?.fullLabel}</span>
                                <Select value={getPlatformDayTime(platform.key, dayValue)} onValueChange={time => setPlatformTime(platform.key, time, dayValue)}>
                                  <SelectTrigger className="w-24 h-8 text-sm bg-white/5 border-white/20">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-60 z-[100] pointer-events-auto" position="popper" sideOffset={4}>
                                    {TIME_OPTIONS.map(time => <SelectItem key={time} value={time}>{time}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>;
                })}
                        </div> : <Select value={schedule.time || '18:00'} onValueChange={time => setPlatformTime(platform.key, time)}>
                          <SelectTrigger className="w-32 bg-white/5 border-white/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 z-[100] pointer-events-auto" position="popper" sideOffset={4}>
                            {TIME_OPTIONS.map(time => <SelectItem key={time} value={time}>{time}</SelectItem>)}
                          </SelectContent>
                        </Select>}
                    </div>}
                </AccordionContent>
              </AccordionItem>;
      })}
        </Accordion>)}
    </div>;
}